import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Dialog,
    Stack,
    alpha
} from '@mui/material';
import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getScreenshots } from '../../../http/complexesApi';

// Иконки
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import DnsIcon from '@mui/icons-material/Dns';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

const ComplexDataXml = ({ host, hostType = 'default' }) => {
    const isDarkMode = useSelector(state => state.settings.darkMode);

    // Тип сервера (avto5/avto6) берём из макроса {$ODBC_BASE}
    const server = useMemo(() => {
        return host?.macros?.find(m => m.macro === '{$ODBC_BASE}')?.value || null;
    }, [host]);

    const [carid, setCarid] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [preview, setPreview] = useState(null);

    // Разделяем ответ на изображения и xml/прочие файлы
    const { images, xmlFiles } = useMemo(() => {
        const files = result?.files || [];
        const images = files.filter(f => (f.mimetype || '').startsWith('image/'));
        const xmlFiles = files.filter(f =>
            (f.mimetype || '').includes('xml') || /\.xml$/i.test(f.name || '')
        );
        return { images, xmlFiles };
    }, [result]);

    const handleSubmit = useCallback(async () => {
        if (!server) {
            setError('Не удалось определить сервер: отсутствует макрос {$ODBC_BASE}');
            return;
        }
        if (!carid.trim()) {
            setError('Укажите CAR ID');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const data = await getScreenshots(server, carid.trim());
            setResult(data);
            if (!data?.files?.length) {
                setError('Файлы не найдены');
            }
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || 'Ошибка запроса';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [server, carid]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleSubmit();
        }
    };

    // Скачивание файла из data-URL (base64)
    const handleDownload = useCallback((file) => {
        const link = document.createElement('a');
        link.href = file.data;
        link.download = file.name || 'data.xml';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    return (
        <Box sx={{
            p: { xs: 1, md: 1.5 },
            width: '100%',
            height: '100%',
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { background: 'var(--background-subtle)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': {
                background: 'var(--border-default)',
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            {/* Карточка запроса */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: '8px', md: '12px' },
                    backgroundColor: 'var(--background-paper)',
                    border: '1px solid var(--border-default)',
                    overflow: 'hidden',
                    mb: { xs: 1, md: 1.5 }
                }}
            >
                {/* Заголовок карточки */}
                <Box sx={{
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, md: 1.5 },
                    backgroundColor: 'var(--background-subtle)',
                    borderBottom: '1px solid var(--border-default)'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 28, md: 32 },
                        height: { xs: 28, md: 32 },
                        borderRadius: { xs: '6px', md: '8px' },
                        backgroundColor: 'var(--primary-color)',
                        color: 'white'
                    }}>
                        <StorageIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                            fontSize: { xs: '0.8rem', md: '0.9rem' },
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            lineHeight: 1.2
                        }}>
                            data.xml
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, color: 'var(--text-muted)', mt: 0.25 }}>
                            Запрос скриншотов и data.xml по CAR ID
                        </Typography>
                    </Box>
                    <Chip
                        icon={<DnsIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                        label={server || 'сервер не определён'}
                        size="small"
                        sx={{
                            height: { xs: 20, md: 22 },
                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                            backgroundColor: server
                                ? alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.12)
                                : alpha('#f44336', 0.12),
                            color: server ? 'var(--primary-color)' : 'var(--error-color)',
                            '& .MuiChip-icon': { color: 'inherit' },
                            '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                        }}
                    />
                </Box>

                {/* Поле ввода и кнопка */}
                <Box sx={{
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: 1, md: 1.5 },
                    flexWrap: 'wrap'
                }}>
                    <TextField
                        label="CAR ID"
                        value={carid}
                        onChange={(e) => setCarid(e.target.value)}
                        onKeyDown={handleKeyDown}
                        size="small"
                        placeholder="Введите CAR ID"
                        disabled={loading}
                        sx={{
                            flex: 1,
                            minWidth: { xs: 140, md: 220 },
                            '& .MuiInputBase-root': {
                                backgroundColor: 'var(--background-paper)',
                                color: 'var(--text-primary)',
                                fontSize: { xs: '0.75rem', md: '0.85rem' }
                            },
                            '& .MuiInputLabel-root': { color: 'var(--text-secondary)', fontSize: { xs: '0.75rem', md: '0.85rem' } },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-default)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' },
                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' }
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || !carid.trim()}
                        startIcon={loading
                            ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
                            : <SearchIcon />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', md: '0.85rem' },
                            height: { xs: 36, md: 40 },
                            backgroundColor: 'var(--primary-color)',
                            '&:hover': { backgroundColor: 'var(--primary-dark)' }
                        }}
                    >
                        {loading ? 'Запрос...' : 'Выполнить'}
                    </Button>
                </Box>
            </Paper>

            {/* Ошибка */}
            {error && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1, md: 1.5 },
                        mb: { xs: 1, md: 1.5 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: { xs: '8px', md: '12px' },
                        backgroundColor: alpha('#f44336', 0.08),
                        border: `1px solid ${alpha('#f44336', 0.3)}`
                    }}
                >
                    <ErrorOutlineIcon sx={{ fontSize: { xs: 18, md: 20 }, color: 'var(--error-color)' }} />
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, color: 'var(--error-color)' }}>
                        {error}
                    </Typography>
                </Paper>
            )}

            {/* Результат */}
            {result && (
                <>
                    {/* Файлы data.xml */}
                    {xmlFiles.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: { xs: '8px', md: '12px' },
                                backgroundColor: 'var(--background-paper)',
                                border: '1px solid var(--border-default)',
                                overflow: 'hidden',
                                mb: { xs: 1, md: 1.5 }
                            }}
                        >
                            <Box sx={{
                                p: { xs: 1, md: 1.5 },
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1, md: 1.5 },
                                backgroundColor: 'var(--background-subtle)',
                                borderBottom: '1px solid var(--border-default)'
                            }}>
                                <DescriptionIcon sx={{ fontSize: { xs: 18, md: 20 }, color: 'var(--primary-color)' }} />
                                <Typography sx={{
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    Файл XML
                                </Typography>
                            </Box>
                            <Box sx={{ p: { xs: 0.75, md: 1 } }}>
                                {xmlFiles.map((file, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            p: { xs: 0.75, md: 1 },
                                            mb: idx < xmlFiles.length - 1 ? 0.5 : 0,
                                            borderRadius: { xs: '6px', md: '8px' },
                                            border: '1px solid var(--border-default)',
                                            backgroundColor: 'var(--background-subtle)'
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                            <DescriptionIcon sx={{ fontSize: 18, color: 'var(--text-muted)' }} />
                                            <Typography sx={{
                                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                                color: 'var(--text-primary)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {file.name}
                                            </Typography>
                                            {file.size != null && (
                                                <Typography sx={{ fontSize: '0.65rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                                    {(file.size / 1024).toFixed(1)} КБ
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => handleDownload(file)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: { xs: '0.7rem', md: '0.75rem' },
                                                color: 'var(--primary-color)',
                                                borderColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.5),
                                                whiteSpace: 'nowrap',
                                                '&:hover': {
                                                    borderColor: 'var(--primary-color)',
                                                    backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.08)
                                                }
                                            }}
                                        >
                                            Скачать
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {/* Фото */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: { xs: '8px', md: '12px' },
                            backgroundColor: 'var(--background-paper)',
                            border: '1px solid var(--border-default)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{
                            p: { xs: 1, md: 1.5 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, md: 1.5 },
                            backgroundColor: 'var(--background-subtle)',
                            borderBottom: '1px solid var(--border-default)'
                        }}>
                            <ImageIcon sx={{ fontSize: { xs: 18, md: 20 }, color: 'var(--primary-color)' }} />
                            <Typography sx={{
                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                fontWeight: 600,
                                color: 'var(--text-primary)'
                            }}>
                                Фото
                            </Typography>
                            <Chip
                                label={images.length}
                                size="small"
                                sx={{
                                    height: { xs: 18, md: 20 },
                                    minWidth: { xs: 20, md: 24 },
                                    fontSize: { xs: '0.6rem', md: '0.65rem' },
                                    fontWeight: 700,
                                    backgroundColor: 'var(--primary-color)',
                                    color: 'white',
                                    '& .MuiChip-label': { px: { xs: 0.4, md: 0.5 } }
                                }}
                            />
                        </Box>

                        <Box sx={{ p: { xs: 0.75, md: 1.5 } }}>
                            {images.length > 0 ? (
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'repeat(2, 1fr)',
                                        sm: 'repeat(3, 1fr)',
                                        lg: 'repeat(4, 1fr)'
                                    },
                                    gap: { xs: 1, md: 1.5 }
                                }}>
                                    {images.map((file, idx) => (
                                        <Box
                                            key={idx}
                                            onClick={() => setPreview(file)}
                                            sx={{
                                                position: 'relative',
                                                borderRadius: { xs: '6px', md: '8px' },
                                                overflow: 'hidden',
                                                border: '1px solid var(--border-default)',
                                                cursor: 'pointer',
                                                backgroundColor: 'var(--background-default)',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    borderColor: 'var(--primary-color)',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: isDarkMode
                                                        ? '0 4px 16px rgba(0,0,0,0.4)'
                                                        : '0 4px 16px rgba(0,0,0,0.15)'
                                                },
                                                '&:hover .dl-btn': { opacity: 1 }
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={file.data}
                                                alt={file.name}
                                                sx={{
                                                    width: '100%',
                                                    height: { xs: 110, md: 140 },
                                                    objectFit: 'cover',
                                                    display: 'block'
                                                }}
                                            />
                                            <Tooltip title="Скачать">
                                                <IconButton
                                                    className="dl-btn"
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        opacity: 0,
                                                        transition: 'opacity 0.15s ease',
                                                        backgroundColor: 'rgba(0,0,0,0.55)',
                                                        color: '#fff',
                                                        width: 26,
                                                        height: 26,
                                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' }
                                                    }}
                                                >
                                                    <DownloadIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Box sx={{
                                                p: { xs: 0.5, md: 0.75 },
                                                borderTop: '1px solid var(--border-default)'
                                            }}>
                                                <Typography sx={{
                                                    fontSize: { xs: '0.6rem', md: '0.65rem' },
                                                    color: 'var(--text-secondary)',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {file.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', color: 'var(--text-muted)', py: { xs: 2, md: 3 } }}>
                                    <ImageNotSupportedIcon sx={{ fontSize: { xs: 36, md: 48 }, mb: 1, opacity: 0.5 }} />
                                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                                        Нет фото
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </>
            )}

            {/* Пустое состояние до запроса */}
            {!result && !error && !loading && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)'
                    }}
                >
                    <DescriptionIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                        Введите CAR ID и выполните запрос
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                        Будут получены файл data.xml и фотографии с сервера {server || '—'}
                    </Typography>
                </Paper>
            )}

            {/* Просмотр фото на весь экран */}
            <Dialog
                open={!!preview}
                onClose={() => setPreview(null)}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        backgroundColor: '#0d1117',
                        m: { xs: 1, md: 2 },
                        borderRadius: { xs: '8px', md: '12px' },
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 1.5, py: 1, backgroundColor: 'rgba(0,0,0,0.4)' }}
                    >
                        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                            {preview?.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Скачать">
                                <IconButton size="small" onClick={() => preview && handleDownload(preview)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
                                    <DownloadIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Закрыть">
                                <IconButton size="small" onClick={() => setPreview(null)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    {preview && (
                        <Box
                            component="img"
                            src={preview.data}
                            alt={preview.name}
                            sx={{
                                display: 'block',
                                maxWidth: '90vw',
                                maxHeight: '80vh',
                                objectFit: 'contain'
                            }}
                        />
                    )}
                </Box>
            </Dialog>
        </Box>
    );
};

export default ComplexDataXml;
